import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import { Heart, X } from 'lucide-react';
import { FlagImage } from '../utils/countryFlags';
import { useUserData } from '../UserDataContext';
import { useContentFilter } from '../../hooks/useContentFilter';
import { isSimilarToMe } from '../utils/similarity';
import { FriendBadge } from './FriendBadge';
import { SimilarBadge } from './SimilarBadge';
import { getUserAvatar } from '../../utils/userUtils';

// Solid dark palette — the likers modal is always dark (matches the request and
// avoids any theme-resolution edge cases that left the panel looking transparent).
const PANEL_BG = '#1a1a1a';
const TEXT_PRIMARY = '#ffffff';
const TEXT_MUTED = 'rgba(255,255,255,0.55)';
const HOVER_BG = 'rgba(255,255,255,0.06)';

interface LikersModalProps {
  open: boolean;
  onClose: () => void;
  /** Direct list of liker user ids (e.g. a reply's likedBy). */
  userIds?: string[];
  /** Async source of liker ids (e.g. a yap's likedBy via YapService.getYapLikers). */
  loadUserIds?: () => Promise<string[]>;
  title?: string;
}

/**
 * Lists the users who liked a piece of content. Sized and spaced to match the
 * member-list / friend-list modals. Colours are applied inline (theme-aware) so
 * the panel/backdrop render reliably regardless of Tailwind JIT class pickup.
 */
export function LikersModal({ open, onClose, userIds, loadUserIds, title = 'Likes' }: LikersModalProps) {
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const { userProfile, isFriend } = useUserData();
  const { isBlockedId } = useContentFilter();

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const ids = loadUserIds ? await loadUserIds() : (userIds || []);
        const unique = Array.from(new Set(ids.map(String).filter(Boolean)));
        if (unique.length === 0) {
          if (!cancelled) setUsers([]);
          return;
        }
        const { UserService } = await import('../../services/api');
        const fetched = await Promise.all(unique.map(id => UserService.fetchUserById(id).catch(() => undefined)));
        if (!cancelled) setUsers(fetched.filter(Boolean));
      } catch {
        if (!cancelled) setUsers([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, userIds, loadUserIds]);

  const openProfile = (user: any) => {
    onClose();
    window.dispatchEvent(new CustomEvent('navigateToFriendProfile', { detail: user }));
  };

  const textPrimary = TEXT_PRIMARY;
  const textMuted = TEXT_MUTED;

  return createPortal(
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-9999 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0"
            style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)' }}
          />
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            className="relative w-full max-w-sm shadow-2xl overflow-hidden flex flex-col rounded-3xl"
            style={{ background: PANEL_BG, maxHeight: '80vh' }}
          >
            {/* Header */}
            <div
              className="p-4 flex items-center justify-between shrink-0"
              style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(231,76,60,0.12)' }}>
                  <Heart style={{ width: 20, height: 20, fill: '#E74C3C', color: '#E74C3C' }} />
                </div>
                <div>
                  <h2 className="text-lg font-bold" style={{ color: textPrimary }}>{title}</h2>
                  <p style={{ fontSize: 10, color: textMuted }}>
                    {loading ? 'Loading…' : `${users.length} like${users.length !== 1 ? 's' : ''}`}
                  </p>
                </div>
              </div>
              <motion.button
                whileTap={{ scale: 0.9 }}
                onClick={onClose}
                className="w-10 h-10 rounded-full flex items-center justify-center transition-colors"
                style={{ background: 'rgba(255,255,255,0.1)' }}
              >
                <X style={{ width: 20, height: 20, color: textPrimary }} />
              </motion.button>
            </div>

            {/* List */}
            <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-2 custom-scrollbar" style={{ WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain' }}>
              {loading ? (
                <div className="flex flex-col items-center justify-center py-10" style={{ color: textMuted }}>
                  <motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}>
                    <Heart style={{ width: 32, height: 32 }} />
                  </motion.div>
                  <p className="text-xs font-bold mt-2">Loading likes...</p>
                </div>
              ) : users.length === 0 ? (
                <div className="text-center py-10" style={{ color: textMuted }}>
                  <Heart style={{ width: 48, height: 48, opacity: 0.2 }} className="mx-auto mb-2" />
                  <p className="text-sm">No likes yet</p>
                </div>
              ) : (
                users.filter((u: any) => !isBlockedId(u?.id)).map((u) => {
                  const isSelf = String(u.id) === String(userProfile.id);
                  const friend = !isSelf && isFriend(String(u.id));
                  const similar = !isSelf && !friend && isSimilarToMe(userProfile, u);
                  return (
                  <div
                    key={String(u.id)}
                    className="flex items-center gap-3 p-2 rounded-2xl cursor-pointer transition-colors"
                    style={{ background: 'transparent' }}
                    onClick={() => openProfile(u)}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = HOVER_BG; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                  >
                    <img
                      src={getUserAvatar(u)}
                      className="w-10 h-10 rounded-full object-cover"
                      style={{ border: '1px solid rgba(255,255,255,0.1)' }}
                      alt={u.name}
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-bold flex items-center gap-1.5" style={{ color: textPrimary }}>
                        <span className="truncate">{u.name || 'User'}</span>
                        {(u.countryFlag || u.country) && (
                          <FlagImage
                            countryName={u.countryFlag || u.country}
                            size="w80"
                            className="w-4 aspect-3/2 object-contain rounded shrink-0"
                          />
                        )}
                      </p>
                      <p className="text-xs truncate" style={{ color: textMuted }}>@{(u.username || '').replace(/^@/, '')}</p>
                    </div>
                    {friend && <FriendBadge className="shrink-0" />}
                    {similar && <SimilarBadge className="shrink-0" />}
                  </div>
                  );
                })
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
}

export default LikersModal;
